<template>
  <div class="ip-auth-container">
    <!-- 闪烁星星层 -->
    <div class="twinkling-stars"></div>
    <div class="auth-modal">
      <div class="auth-header">
        <h2>IP授权验证</h2>
        <div class="auth-status" :class="authStatusClass">
          {{ authStatusText }}
        </div>
      </div>
      
      <div class="auth-content">
        <div class="ip-info">
          <div class="info-item">
            <span class="label">本机IP地址:</span>
            <span class="value" :class="{ 'loading': isLoadingLocalIp }">
              {{ isLoadingLocalIp ? '获取中...' : localIp || '获取失败' }}
            </span>
          </div>
          
          <!-- 授权IP列表已隐藏，保护敏感信息 -->
        </div>
        
        <div class="auth-actions">
          <el-button 
            type="primary" 
            @click="verifyIpPermanent " 
            :loading="isVerifying"
            :disabled="!localIp"
            size="large"
          >
            {{ isVerifying ? '验证中...' : '开始验证' }}
          </el-button>
          
          <el-button 
            @click="refreshLocalIp" 
            :loading="isLoadingLocalIp"
            size="large"
          >
            刷新本机IP
          </el-button>
          
          <el-button 
            type="success"
            @click="copyLocalIp" 
            :disabled="!localIp"
            size="large"
            icon="el-icon-copy-document"
          >
            复制本机IP
          </el-button>
        </div>
        
        <div class="auth-tips">
          <p><i class="el-icon-info"></i> 系统将验证您的IP地址是否在授权列表中</p>
          <p><i class="el-icon-warning"></i> 如需添加IP授权，请联系管理员</p>
        </div>
      </div>
      
      <!-- 验证失败提示 -->
      <div v-if="showErrorMessage" class="error-message">
        <i class="el-icon-warning"></i>
        <span>{{ errorMessage }}</span>
      </div>
      
      <!-- 卡密激活区域 -->
      <div v-if="showCardActivation" class="card-activation">
        <div class="activation-header">
          <h3>卡密激活</h3>
          <p>IP验证失败，请输入卡密进行激活</p>
        </div>
        
        <div class="activation-form">
          <el-input
            ref="cardKeyInput"
            v-model="cardKey"
            placeholder="请输入卡密"
            size="large"
            :disabled="isActivating"
            @keyup.enter="activateCard"
            @focus="handleInputFocus"
            @click="handleInputClick"
            clearable
          >
            <template #prepend>
              <i class="el-icon-key"></i>
            </template>
          </el-input>
          
          <!-- 状态提示 -->
          <div v-if="isActivating" class="activation-status">
            <i class="el-icon-loading"></i>
            <span>正在处理中...</span>
          </div>
          
          <div class="activation-buttons">
            <el-button 
              type="primary" 
              @click="activateCard" 
              :loading="isActivating"
              :disabled="!cardKey.trim()"
              size="large"
            >
              {{ isActivating ? '激活中...' : '激活卡密' }}
            </el-button>
            
            <el-button 
              @click="hideCardActivation" 
              :disabled="isActivating"
              size="large"
            >
              取消
            </el-button>
          </div>
        </div>
        
        <!-- 卡密状态提示 -->
        <div v-if="cardStatusMessage" class="card-status-message" :class="cardStatusClass">
          <i :class="cardStatusIcon"></i>
          <span v-html="cardStatusMessage"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ipAuthService from '@/services/ipAuthService'
import CryptoUtils from '@/utils/cryptoUtils'
import proxyConfig from '@/config/proxyConfig'

export default {
  name: 'IpAuthModal',
  data() {
    return {
      localIp: '',
      allowedIps: [],
      isVerifying: false,
      isLoadingLocalIp: false,
      authStatus: 'pending', // pending, success, failed
      errorMessage: '',
      showErrorMessage: false,
      verificationTimer: null,
      verificationFailCount: 0, // 定期验证失败计数器
      maxFailCount: 6, // 最大失败次数
      cryptoUtils: new CryptoUtils(), // 初始化加密工具
      
      // 卡密激活相关
      showCardActivation: false, // 是否显示卡密激活区域
      cardKey: '', // 卡密输入值
      isActivating: false, // 是否正在激活
      cardStatusMessage: '', // 卡密状态提示信息
      cardStatusClass: '', // 卡密状态样式类
      cardStatusIcon: '' // 卡密状态图标
    }
  },
  computed: {
    authStatusText() {
      switch (this.authStatus) {
        case 'pending':
          return '待验证'
        case 'success':
          return '验证通过'
        case 'failed':
          return '验证失败'
        default:
          return '未知状态'
      }
    },
    authStatusClass() {
      return {
        'status-pending': this.authStatus === 'pending',
        'status-success': this.authStatus === 'success',
        'status-failed': this.authStatus === 'failed'
      }
    }
  },
  mounted() {
    this.getLocalIp().then(() => {
      // 获取IP后自动进行一次验证
      if (this.localIp) {
        setTimeout(() => {
          this.verifyIpPermanent ()
        }, 1000) // 延迟1秒进行验证，让用户看到IP获取过程
      }
    })
  },
  beforeDestroy() {
    // 清除定时器
    if (this.verificationTimer) {
      clearInterval(this.verificationTimer)
    }
  },
  methods: {
    // 获取本机IP地址
    async getLocalIp() {
      this.isLoadingLocalIp = true
      try {
        // 优先使用WebRTC获取公网IP
        const webrtcIp = await this.getLocalIpViaWebRTC()
        if (webrtcIp) {
          this.localIp = webrtcIp
          return
        }
        
        // WebRTC获取失败，使用HTTP接口作为兜底方案
        const publicIp = await this.getPublicIpViaHttp()
        if (publicIp) {
          this.localIp = publicIp
        } else {
          throw new Error('无法获取IP地址')
        }
      } catch (error) {
        this.showError('获取本机IP地址失败，请检查网络连接')
      } finally {
        this.isLoadingLocalIp = false
      }
    },
    
    // 通过HTTP接口获取公网IP（兜底方案）
    async getPublicIpViaHttp() {
      // 多个备用接口，提高成功率
      const services = [
        {
          url: 'https://httpbin.org/ip',
          parser: (data) => data.origin
        },
        {
          url: 'https://api.ipify.org?format=json',
          parser: (data) => data.ip
        },
        {
          url: 'https://ipapi.co/ip/',
          parser: (data) => typeof data === 'string' ? data.trim() : data,
          isText: true
        }
      ]
      
      for (const service of services) {
        try {
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 3000)
          
          const response = await fetch(service.url, {
            method: 'GET',
            signal: controller.signal
          })
          
          clearTimeout(timeoutId)
          
          if (!response.ok) continue
          
          const data = service.isText ? await response.text() : await response.json()
          const ip = service.parser(data)
          
          if (ip && this.isValidIp(ip)) {
            return ip
          }
        } catch (error) {
          // 继续尝试下一个服务
          continue
        }
      }
      
      return null
    },
    
    // 验证IP地址格式
    isValidIp(ip) {
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
      return ipRegex.test(ip)
    },
    
    // 通过WebRTC获取所有可能的IP地址
    getLocalIpViaWebRTC() {
      return new Promise((resolve) => {
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' }
          ]
        })
        
        const ips = new Set() // 使用Set避免重复IP
        let candidateCount = 0
        const maxCandidates = 10 // 最多收集10个候选
        
        pc.createDataChannel('')
        pc.createOffer()
          .then(offer => pc.setLocalDescription(offer))
          .catch(error => {
            pc.close()
            resolve(null)
          })
        
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidate = event.candidate.candidate
            const ipMatch = candidate.match(/(\d+\.\d+\.\d+\.\d+)/)
            if (ipMatch && !ipMatch[1].startsWith('127.') && !ipMatch[1].startsWith('192.') && !ipMatch[1].startsWith('10.')) {
              ips.add(ipMatch[1])
              candidateCount++
              
              // 如果收集到足够的候选或者已经有公网IP，提前结束
              if (candidateCount >= maxCandidates || this.isPublicIp(ipMatch[1])) {
                pc.close()
                resolve(this.selectBestIp(Array.from(ips)))
              }
            }
          }
        }
        
        // 超时处理 - 延长到5秒以收集更多候选
        setTimeout(() => {
          pc.close()
          if (ips.size > 0) {
            resolve(this.selectBestIp(Array.from(ips)))
          } else {
            resolve(null)
          }
        }, 5000)
      })
    },
    
    // 判断是否为公网IP
    isPublicIp(ip) {
      const parts = ip.split('.').map(Number)
      
      // 私有IP地址范围
      if (parts[0] === 10) return false // 10.0.0.0/8
      if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return false // 172.16.0.0/12
      if (parts[0] === 192 && parts[1] === 168) return false // 192.168.0.0/16
      if (parts[0] === 169 && parts[1] === 254) return false // 169.254.0.0/16 (链路本地)
      
      return true
    },
    
    // 选择最佳IP地址
    selectBestIp(ips) {
      if (ips.length === 0) return null
      
      // 优先选择公网IP
      const publicIps = ips.filter(ip => this.isPublicIp(ip))
      if (publicIps.length > 0) {
        return publicIps[0] // 返回第一个公网IP
      }
      
      // 如果没有公网IP，按优先级选择私有IP
      // 优先级：192.168.x.x > 10.x.x.x > 172.16-31.x.x
      const privateIps = ips.filter(ip => !this.isPublicIp(ip))
      const sorted = privateIps.sort((a, b) => {
        const aFirst = parseInt(a.split('.')[0])
        const bFirst = parseInt(b.split('.')[0])
        
        if (aFirst === 192 && bFirst !== 192) return -1
        if (bFirst === 192 && aFirst !== 192) return 1
        if (aFirst === 10 && bFirst === 172) return -1
        if (bFirst === 10 && aFirst === 172) return 1
        
        return 0
      })
      
      return sorted[0] || ips[0]
    },
    
    // 已移除公网IP获取方法，避免安全风险
    
    // 刷新本机IP
    refreshLocalIp() {
      this.localIp = ''
      this.getLocalIp()
    },
    
    // 验证IP地址
    async verifyIpPermanent () {
      if (!this.localIp) {
        this.showError('请先获取本机IP地址')
        return
      }
      
      this.isVerifying = true
      this.hideError()
      
      try {
        // 创建加密的请求参数
        const encryptedPayload = this.cryptoUtils.createEncryptedPayload(this.localIp)
        
        // 根据代理配置获取IP验证接口URL
        const apiUrl = proxyConfig.getIpVerifyUrl()
        
        // 从 URL 中提取域名和端口信息
        const urlObj = new URL(apiUrl)
        const hostWithPort = urlObj.port ? `${urlObj.hostname}:${urlObj.port}` : urlObj.hostname
        const origin = `${urlObj.protocol}//${hostWithPort}`
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Host': hostWithPort,
            'Content-Type': 'application/x-www-form-urlencoded',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36',
            'Accept': '*/*',
            'Origin': origin,
            'Sec-Fetch-Site': 'same-origin',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Dest': 'empty',
            'Referer': `${origin}/`,
            'Accept-Language': 'zh-CN,zh;q=0.9'
          },
          body: `data=${encodeURIComponent(encryptedPayload)}`
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        // 获取后端返回的JSON响应
        const jsonResponse = await response.text()
        
        try {
          // 解析JSON响应
          const responseObj = JSON.parse(jsonResponse)
          
          // 检查响应码
          if (responseObj.code === 200) {
            // 验证成功，data字段包含RSA加密后的数据，需要解密
            const encryptedData = responseObj.data
            
            try {
              // 使用RSA公钥解密后端返回的数据
              const decryptedData = this.cryptoUtils.rsaDecrypt(encryptedData)
              
              // 使用新的验证方法：检查时间戳（10秒内）和IP匹配
              const validationResult = this.cryptoUtils.validateDecryptedData(decryptedData, this.localIp)
              
              if (validationResult.success) {
                this.authStatus = 'success'
                this.$message.success('IP验证通过，正在跳转...')
              } else {
                this.authStatus = 'failed'
                this.showError(`验证失败: ${validationResult.message}`)
                return
              }
            } catch (decryptError) {
              this.authStatus = 'failed'
              this.showError('认证失败')
              return
            }
          } else {
            // 验证失败，显示后端返回的具体错误信息，并显示卡密激活区域
            this.authStatus = 'failed'
            const errorMessage = responseObj.message || '认证失败'
            this.showError(errorMessage)
            // 显示卡密激活区域
            this.showCardActivation = true
            return
          }
        } catch (parseError) {
          this.authStatus = 'failed'
          this.showError('认证失败')
          return
        }
        
        // 验证通过的处理
        if (this.authStatus === 'success') {
          // 更新ipAuthService状态
          ipAuthService.isAuthenticated = true
          ipAuthService.localIp = this.localIp
          ipAuthService.allowedIps = [this.localIp] // 使用当前IP
          
          // 在ipAuthService中启动定时验证，解决组件销毁问题
          ipAuthService.startPeriodicVerification()
          
          // 延迟跳转，确保状态更新完成
          setTimeout(() => {
            this.$emit('auth-success')
          }, 500)
        }
      } catch (error) {
        this.authStatus = 'failed'
        this.showError('认证失败')
      } finally {
        this.isVerifying = false
      }
    },
    

    
    // 显示错误信息
    showError(message) {
      this.errorMessage = message
      this.showErrorMessage = true
    },
    
    // 隐藏错误信息
    hideError() {
      this.showErrorMessage = false
      this.errorMessage = ''
    },
    
    // 复制本机IP到剪贴板
    async copyLocalIp() {
      if (!this.localIp) {
        this.$message.warning('没有可复制的IP地址')
        return
      }
      
      try {
        // 优先使用现代的 Clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.localIp)
          this.$message.success('IP地址已复制到剪贴板')
        } else {
          // 降级方案：使用传统的 document.execCommand
          const textArea = document.createElement('textarea')
          textArea.value = this.localIp
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          
          try {
            const successful = document.execCommand('copy')
            if (successful) {
              this.$message.success('IP地址已复制到剪贴板')
            } else {
              throw new Error('复制失败')
            }
          } finally {
            document.body.removeChild(textArea)
          }
        }
      } catch (error) {
        console.error('复制失败:', error)
        this.$message.error('复制失败，请手动复制IP地址')
      }
    },
    
    // 隐藏卡密激活区域
    hideCardActivation() {
      this.showCardActivation = false
      this.cardKey = ''
      this.cardStatusMessage = ''
      this.cardStatusClass = ''
      this.cardStatusIcon = ''
      this.isActivating = false // 确保重置激活状态
    },
    
    // 处理输入框焦点事件
    handleInputFocus() {
      console.log('卡密输入框获得焦点')
      // 如果状态异常，重置为正常状态
      if (this.isActivating && !this.cardStatusMessage) {
        console.log('检测到异常状态，重置为正常')
        this.isActivating = false
      }
    },
    
    // 处理输入框点击事件
    handleInputClick() {
      console.log('卡密输入框被点击')
      // 确保输入框可用
      if (this.isActivating && !this.cardStatusMessage) {
        console.log('强制重置激活状态')
        this.isActivating = false
      }
      // 自动焦点到输入框
      this.$nextTick(() => {
        if (this.$refs.cardKeyInput) {
          this.$refs.cardKeyInput.focus()
        }
      })
    },
    
    // 查询卡密状态
    async queryCardStatus(cardKey) {
      console.log('开始查询卡密状态:', cardKey)
      try {
        const apiUrl = proxyConfig.getCardStatusUrl()

        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Host': 'hongchen8.asia:19107',
            'Content-Type': 'application/x-www-form-urlencoded',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36',
            'Accept': '*/*',
            'Origin': 'https://hongchen8.asia:19107',
            'Sec-Fetch-Site': 'same-origin',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Dest': 'empty',
            'Referer': 'https://hongchen8.asia:19107/',
            'Accept-Language': 'zh-CN,zh;q=0.9'
          },
          body: `cardKey=${encodeURIComponent(cardKey.trim())}`
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const responseText = await response.text()
        const result = JSON.parse(responseText)
        return result
      } catch (error) {
        throw error
      }
    },
    
    // 激活卡密
    async activateCardWithIp(cardKey, ipAddress) {
      try {
        const apiUrl = proxyConfig.getCardActivateUrl()
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Host': 'hongchen8.asia:19107',
            'Content-Type': 'application/x-www-form-urlencoded',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36',
            'Accept': '*/*',
            'Origin': 'https://hongchen8.asia:19107',
            'Sec-Fetch-Site': 'same-origin',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Dest': 'empty',
            'Referer': 'https://hongchen8.asia:19107/',
            'Accept-Language': 'zh-CN,zh;q=0.9'
          },
          body: `cardKey=${encodeURIComponent(cardKey.trim())}&ipAddress=${encodeURIComponent(ipAddress)}`
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const responseText = await response.text()
        return JSON.parse(responseText)
      } catch (error) {
        throw error
      }
    },
    
    // 激活卡密主方法
    async activateCard() {
      if (!this.cardKey.trim()) {
        this.$message.warning('请输入卡密')
        return
      }
      
      if (!this.localIp) {
        this.$message.warning('请先获取本机IP地址')
        return
      }
      
      this.isActivating = true
      this.cardStatusMessage = ''
      
      try {
        // 先查询卡密状态
        const statusResult = await this.queryCardStatus(this.cardKey)
        
        if (statusResult.code === 1000) {
          // 卡密不存在
          this.showCardStatus('error', 'el-icon-error', statusResult.message)
          return
        } else if (statusResult.code === 1002) {
          // 卡密已禁用
          this.showCardStatus('error', 'el-icon-error', statusResult.message)
          return
        } else if (statusResult.code === 1003) {
          // 卡密已过期
          this.showCardStatus('error', 'el-icon-error', statusResult.message)
          return
        } else if (statusResult.code === 1001) {
          // 卡密未激活，弹窗确认后激活
          this.isActivating = false // 先重置状态，让弹窗能正常显示
          
          // 使用原生confirm替换ElementUI的$confirm
          const userConfirmed = confirm(statusResult.message + '\n\n点击“确定”立即激活，点击“取消”放弃操作')
          
          if (userConfirmed) {
            this.isActivating = true // 重新设置加载状态
            
            try {
              const activateResult = await this.activateCardWithIp(this.cardKey, this.localIp)
              
              if (activateResult.code === 200) {
                // 激活成功
                this.showCardStatus('success', 'el-icon-success', '卡密激活成功！正在重新验证IP...')
                
                // 延迟2秒后重新验证IP
                setTimeout(() => {
                  this.hideCardActivation()
                  this.verifyIpPermanent ()
                }, 2000)
              } else {
                this.showCardStatus('error', 'el-icon-error', activateResult.message || '激活失败')
              }
            } catch (error) {
              this.showCardStatus('error', 'el-icon-error', '卡密激活失败，请重试')
            }
            
            this.isActivating = false
          } else {
            this.isActivating = false
          }
          return // 提前返回，不要进入finally块
        } else if (statusResult.code === 1004) {
          // 卡密已激活，询问是否替换IP
          this.isActivating = false // 先重置状态，让弹窗能正常显示
          
          // 使用原生confirm替换ElementUI的$confirm
          const userConfirmed = confirm(statusResult.message + '\n\n点击“确定”替换IP，点击“取消”放弃操作')
          
          if (userConfirmed) {
            this.isActivating = true // 重新设置加载状态
            
            try {
              const activateResult = await this.activateCardWithIp(this.cardKey, this.localIp)
              
              if (activateResult.code === 200) {
                this.showCardStatus('success', 'el-icon-success', 'IP替换成功！正在重新验证IP...')
                
                setTimeout(() => {
                  this.hideCardActivation()
                  this.verifyIpPermanent ()
                }, 2000)
              } else {
                this.showCardStatus('error', 'el-icon-error', activateResult.message || 'IP替换失败')
              }
            } catch (error) {
              console.error('IP替换失败:', error)
              this.showCardStatus('error', 'el-icon-error', 'IP替换失败，请重试')
            }
            
            this.isActivating = false
          } else {
            console.log('用户取消替换IP')
            this.isActivating = false
          }
          return // 提前返回，不要进入finally块
        } else if (statusResult.code === 1005) {
          // 今天已解绑过，需要扣8小时，询问用户是否继续
          this.isActivating = false // 先重置状态，让弹窗能正常显示
          
          // 使用原生confirm替换ElementUI的$confirm
          const userConfirmed = confirm(statusResult.message + '\n\n点击"确定"继续解绑（扣除8小时），点击"取消"放弃操作')
          
          if (userConfirmed) {
            this.isActivating = true // 重新设置加载状态
            
            try {
              const activateResult = await this.activateCardWithIp(this.cardKey, this.localIp)
              
              if (activateResult.code === 200) {
                this.showCardStatus('success', 'el-icon-success', 'IP替换成功！已扣除8小时有效期，正在重新验证IP...')
                
                setTimeout(() => {
                  this.hideCardActivation()
                  this.verifyIpPermanent ()
                }, 2000)
              } else {
                this.showCardStatus('error', 'el-icon-error', activateResult.message || 'IP替换失败')
              }
            } catch (error) {
              console.error('IP替换失败:', error)
              this.showCardStatus('error', 'el-icon-error', 'IP替换失败，请重试')
            }
            
            this.isActivating = false
          } else {
            console.log('用户取消解绑操作')
            this.isActivating = false
          }
          return // 提前返回，不要进入finally块
        } else {
          // 其他错误
          this.showCardStatus('error', 'el-icon-error', statusResult.message || '查询卡密状态失败')
        }
      } catch (error) {
        this.showCardStatus('error', 'el-icon-error', '网络请求失败，请检查网络连接')
      } finally {
        this.isActivating = false
      }
    },
    
    // 显示卡密状态信息
    showCardStatus(type, icon, message) {
      this.cardStatusClass = `status-${type}`
      this.cardStatusIcon = icon
      this.cardStatusMessage = message
    }
  }
}
</script>

<style scoped>
/* 动态背景容器 */
.ip-auth-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #0f0f23, #1a1a2e, #16213e, #0f3460);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: hidden;
}

/* 动态渐变背景动画 */
@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* 添加星星动态背景 */
.ip-auth-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    /* 大星星 */
    radial-gradient(3px 3px at 15% 20%, rgba(255, 255, 255, 0.8), transparent 50%),
    radial-gradient(2px 2px at 85% 15%, rgba(100, 224, 208, 0.6), transparent 50%),
    radial-gradient(4px 4px at 70% 80%, rgba(138, 43, 226, 0.7), transparent 50%),
    radial-gradient(2px 2px at 25% 70%, rgba(100, 149, 237, 0.6), transparent 50%),
    radial-gradient(3px 3px at 90% 60%, rgba(255, 255, 255, 0.5), transparent 50%),
    /* 中等星星 */
    radial-gradient(2px 2px at 40% 30%, rgba(255, 255, 255, 0.4), transparent 50%),
    radial-gradient(1px 1px at 60% 25%, rgba(100, 224, 208, 0.5), transparent 50%),
    radial-gradient(2px 2px at 30% 85%, rgba(138, 43, 226, 0.4), transparent 50%),
    radial-gradient(1px 1px at 80% 40%, rgba(100, 149, 237, 0.5), transparent 50%),
    /* 小星星 */
    radial-gradient(1px 1px at 20px 30px, rgba(255, 255, 255, 0.3), transparent),
    radial-gradient(1px 1px at 40px 70px, rgba(100, 224, 208, 0.3), transparent),
    radial-gradient(1px 1px at 90px 40px, rgba(138, 43, 226, 0.3), transparent),
    radial-gradient(1px 1px at 130px 80px, rgba(100, 149, 237, 0.3), transparent),
    radial-gradient(1px 1px at 160px 30px, rgba(255, 255, 255, 0.2), transparent);
  background-repeat: repeat;
  background-size: 300px 200px;
  animation: starsMove 25s linear infinite;
  z-index: 1;
}

/* 星星移动动画 */
@keyframes starsMove {
  from {
    transform: translateX(0) translateY(0);
  }
  to {
    transform: translateX(300px) translateY(-100px);
  }
}

/* 添加闪烁星星层 */
.ip-auth-container .twinkling-stars {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(2px 2px at 10% 10%, rgba(255, 255, 255, 0.9), transparent 50%),
    radial-gradient(1px 1px at 90% 20%, rgba(100, 224, 208, 0.8), transparent 50%),
    radial-gradient(2px 2px at 30% 90%, rgba(138, 43, 226, 0.7), transparent 50%),
    radial-gradient(1px 1px at 80% 80%, rgba(100, 149, 237, 0.8), transparent 50%),
    radial-gradient(2px 2px at 50% 50%, rgba(255, 255, 255, 0.6), transparent 50%);
  background-repeat: repeat;
  background-size: 400px 300px;
  animation: twinkle 3s ease-in-out infinite alternate;
  z-index: 2;
}

/* 星星闪烁动画 */
@keyframes twinkle {
  0% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1.2);
  }
}

/* 添加流动光效 */
.ip-auth-container::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(64, 224, 208, 0.1),
    rgba(64, 224, 208, 0.2),
    rgba(64, 224, 208, 0.1),
    transparent
  );
  animation: lightFlow 6s ease-in-out infinite;
  z-index: 2;
}

/* 光效流动动画 */
@keyframes lightFlow {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: -100%;
  }
}

/* 模态框样式和动画 */
.auth-modal {
  background: rgba(42, 42, 42, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  min-width: 500px;
  max-width: 600px;
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(64, 224, 208, 0.3);
  position: relative;
  z-index: 10;
  animation: modalSlideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

/* 模态框入场动画 */
@keyframes modalSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 模态框边框光效 */
.auth-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 20px;
  padding: 2px;
  background: linear-gradient(
    45deg,
    rgba(64, 224, 208, 0.5),
    rgba(100, 149, 237, 0.5),
    rgba(138, 43, 226, 0.5),
    rgba(64, 224, 208, 0.5)
  );
  background-size: 300% 300%;
  animation: borderGlow 4s ease infinite;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

/* 边框光效动画 */
@keyframes borderGlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.auth-header {
  text-align: center;
  margin-bottom: 30px;
}

/* 标题样式和动画 */
.auth-header h2 {
  color: #ffffff;
  font-size: 24px;
  margin: 0 0 15px 0;
  font-weight: 600;
  background: linear-gradient(
    45deg,
    #64e0d0,
    #6495ed,
    #8a2be2,
    #64e0d0
  );
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: textGlow 3s ease-in-out infinite;
  text-shadow: 0 0 20px rgba(100, 224, 208, 0.3);
}

/* 文字光效动画 */
@keyframes textGlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* 状态标签样式和动画 */
.auth-status {
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  animation: statusPulse 2s ease-in-out infinite;
}

/* 状态脉冲动画 */
@keyframes statusPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 167, 38, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 167, 38, 0);
  }
}

/* 状态标签光效 */
.auth-status::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: statusShine 2s ease-in-out infinite;
}

/* 状态光效动画 */
@keyframes statusShine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.status-pending {
  background: #ffa726;
  color: #fff;
}

.status-success {
  background: #4caf50;
  color: #fff;
}

.status-failed {
  background: #f44336;
  color: #fff;
}

.auth-content {
  margin-bottom: 20px;
}

/* IP信息区域样式和动画 */
.ip-info {
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.9), rgba(42, 42, 42, 0.9));
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid rgba(64, 224, 208, 0.2);
  position: relative;
  overflow: hidden;
  animation: infoSlideIn 0.8s ease-out 0.3s both;
}

/* IP信息区域入场动画 */
@keyframes infoSlideIn {
  0% {
    opacity: 0;
    transform: translateX(-30px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

/* IP信息区域光效背景 */
.ip-info::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(64, 224, 208, 0.05),
    rgba(100, 149, 237, 0.05),
    rgba(138, 43, 226, 0.05)
  );
  background-size: 200% 200%;
  animation: infoGlow 6s ease infinite;
  z-index: -1;
}

/* IP信息光效动画 */
@keyframes infoGlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #cccccc;
  font-weight: 500;
  min-width: 120px;
  font-size: 14px;
}

.value {
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
}

/* 加载状态样式增强 */
.value.loading {
  color: #ffa726;
  position: relative;
  animation: loadingPulse 1.5s ease-in-out infinite;
}

/* 加载脉冲动画 */
@keyframes loadingPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* 加载状态光点效果 */
.value.loading::after {
  content: '';
  position: absolute;
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: #ffa726;
  border-radius: 50%;
  animation: loadingDot 1s ease-in-out infinite;
}

/* 加载光点动画 */
@keyframes loadingDot {
  0%, 100% {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.ip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: 10px;
}

.ip-item {
  background: #404040;
  color: #ffffff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  border: 1px solid #555555;
}

.ip-item.current {
  background: #4caf50;
  border-color: #4caf50;
}

/* 操作按钮区域样式和动画 */
.auth-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 25px;
  animation: actionsSlideIn 1s ease-out 0.6s both;
}

/* 操作按钮区域入场动画 */
@keyframes actionsSlideIn {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 按钮悬停效果增强 */
.auth-actions .el-button {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.auth-actions .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(64, 224, 208, 0.3);
}

.auth-actions .el-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s;
}

.auth-actions .el-button:hover::before {
  left: 100%;
}

/* 提示信息区域样式和动画 */
.auth-tips {
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.9), rgba(33, 150, 243, 0.1));
  border-radius: 12px;
  padding: 15px;
  border-left: 4px solid #2196f3;
  position: relative;
  overflow: hidden;
  animation: tipsSlideIn 1.2s ease-out 0.9s both;
}

/* 提示信息入场动画 */
@keyframes tipsSlideIn {
  0% {
    opacity: 0;
    transform: translateX(30px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

/* 提示信息光效 */
.auth-tips::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(
    to bottom,
    #2196f3,
    #64e0d0,
    #2196f3
  );
  animation: tipsBorderGlow 3s ease-in-out infinite;
}

/* 提示边框光效动画 */
@keyframes tipsBorderGlow {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.auth-tips p {
  color: #cccccc;
  margin: 8px 0;
  font-size: 13px;
  display: flex;
  align-items: center;
}

.auth-tips i {
  margin-right: 8px;
  color: #2196f3;
}

.error-message {
  background: #f44336;
  color: #ffffff;
  padding: 12px 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  margin-top: 15px;
}

.error-message i {
  margin-right: 8px;
  font-size: 16px;
}

/* 卡密激活区域样式 */
.card-activation {
  background: #1a1a1a;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  border: 1px solid #404040;
}

.activation-header {
  text-align: center;
  margin-bottom: 20px;
}

.activation-header h3 {
  color: #ffffff;
  font-size: 18px;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.activation-header p {
  color: #cccccc;
  font-size: 14px;
  margin: 0;
}

.activation-form {
  margin-bottom: 15px;
}

.activation-form .el-input {
  margin-bottom: 15px;
}

.activation-status {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffa726;
  font-size: 14px;
  margin: 10px 0;
  padding: 8px;
  background: rgba(255, 167, 38, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(255, 167, 38, 0.3);
}

.activation-status i {
  margin-right: 8px;
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.activation-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.card-status-message {
  padding: 12px 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  margin-top: 15px;
  font-size: 14px;
}

.card-status-message i {
  margin-right: 8px;
  font-size: 16px;
}

.card-status-message.status-success {
  background: #4caf50;
  color: #ffffff;
}

.card-status-message.status-error {
  background: #f44336;
  color: #ffffff;
}

.card-status-message.status-warning {
  background: #ff9800;
  color: #ffffff;
}

/* 响应式设计 */
@media screen and (max-width: 768px) {
  .auth-modal {
    min-width: 90%;
    margin: 20px;
    padding: 20px;
  }
  
  .auth-header h2 {
    font-size: 20px;
  }
  
  .auth-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .label {
    min-width: auto;
    margin-bottom: 5px;
  }
  
  .ip-list {
    margin-left: 0;
    margin-top: 5px;
  }
  
  /* 卡密激活区域移动端样式 */
  .activation-buttons {
    flex-direction: column;
    gap: 10px;
  }
  
  .activation-header h3 {
    font-size: 16px;
  }
  
  .activation-header p {
    font-size: 13px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/IpAuthModal.vue